import { z } from 'zod';
/**
 * The API response envelope.
 *
 * Every endpoint answers with exactly one of these two shapes. Defining it once
 * here rather than in the API means the admin panel and the marketing site
 * cannot drift from what the server actually sends — a mismatch becomes a
 * compile error in the consumer rather than a runtime `undefined` in a table.
 */
export const ApiErrorSchema = z.object({
    /** Machine-readable and stable. Clients branch on this, never on the message. */
    code: z.string(),
    /** One or two sentences a non-technical person can act on. */
    message: z.string(),
    /** Field name → messages, for a 422 from a Zod-validated body. */
    fields: z.record(z.array(z.string())).optional(),
    /** Correlation id, echoed from `X-Request-Id`. */
    requestId: z.string().optional(),
});
/**
 * Cursor pagination.
 *
 * Cursors rather than page numbers everywhere a list can grow while it is
 * being read — the gate log, the activity feed, chat. With `?page=2`, a busy
 * gate shows the same visitor twice and skips another.
 */
export const PageMetaSchema = z.object({
    nextCursor: z.string().nullable().optional(),
    hasMore: z.boolean().default(false),
    /** Total is optional: on very large tables counting costs more than it is worth. */
    total: z.number().int().nonnegative().optional(),
});
export const okEnvelope = (data) => z.object({
    ok: z.literal(true),
    data,
    meta: PageMetaSchema.optional(),
});
export const errorEnvelope = z.object({
    ok: z.literal(false),
    error: ApiErrorSchema,
});
export const envelope = (data) => z.discriminatedUnion('ok', [okEnvelope(data), errorEnvelope]);
/** Narrows an envelope to its success branch. */
export const isOk = (envelope) => envelope.ok;
/**
 * Every error code the API can return.
 *
 * Exhaustive on purpose: a client that switches over this gets a compile error
 * when a new one is added, which is the only reliable way to keep error
 * handling in step across four surfaces.
 */
export const ERROR_CODES = [
    // Auth
    'UNAUTHENTICATED',
    'TOKEN_EXPIRED',
    'REFRESH_TOKEN_INVALID',
    'OTP_INVALID',
    'OTP_EXPIRED',
    'OTP_RATE_LIMITED',
    'ACCOUNT_SUSPENDED',
    'WRONG_PLATFORM',
    // Authorisation and tenancy
    'FORBIDDEN',
    'ROLE_REQUIRED',
    'TENANT_MISMATCH',
    'MISSING_TENANT',
    // Plan and subscription
    'PLAN_LIMIT',
    'FEATURE_NOT_IN_PLAN',
    'FEATURE_FLAG_OFF',
    'SOCIETY_READ_ONLY',
    'SUBSCRIPTION_SUSPENDED',
    'TRIAL_EXPIRED',
    // Requests
    'VALIDATION_FAILED',
    'NOT_FOUND',
    'CONFLICT',
    'IDEMPOTENCY_KEY_REUSED',
    'RATE_LIMITED',
    'PAYLOAD_TOO_LARGE',
    // Domain
    'PASS_EXPIRED',
    'PASS_ALREADY_USED',
    'PASS_REVOKED',
    'WRONG_GATE',
    'SLOT_TAKEN',
    'BILL_ALREADY_PAID',
    'PAYMENT_AMOUNT_MISMATCH',
    'GATEWAY_ERROR',
    'LEDGER_UNBALANCED',
    // Consent
    'CONSENT_REQUIRED',
    'CONSENT_VERSION_STALE',
    // Server
    'INTERNAL_ERROR',
    'SERVICE_UNAVAILABLE',
];
//# sourceMappingURL=envelope.js.map